import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import type { PlainRoleAssignment } from "@/queries/group-queries";

/**
 * Checks if there are any changes to role assignments for a specific resource type.
 *
 * Compares (resourceId, resourceRole) pairs between old and new assignments to detect:
 * - Additions: New resource permissions that didn't exist before
 * - Removals: Resource permissions that were removed
 * - Role changes: Same resource but different role
 *
 * Uses Set-based comparison for O(n) performance where n is the number of assignments.
 *
 * @param oldAssignments - Previous role assignments
 * @param newAssignments - Current role assignments
 * @param resourceType - Type of resource to check (PROJECT, RUNNER, etc.)
 * @returns true if any changes detected, false if identical
 *
 * @example
 * ```typescript
 * const changed = hasResourceTypeChanges(
 *   oldAssignments,
 *   newAssignments,
 *   ResourceType.PROJECT
 * );
 * if (changed) {
 *   // Invalidate project list
 * }
 * ```
 */
export function hasResourceTypeChanges(
    oldAssignments: PlainRoleAssignment[],
    newAssignments: PlainRoleAssignment[],
    resourceType: ResourceType,
): boolean {
    // Extract (resourceId, role) pairs for this resource type
    const oldIds = new Set(
        oldAssignments.filter((a) => a.resourceType === resourceType).map((a) => `${a.resourceId}:${a.resourceRole}`),
    );

    const newIds = new Set(
        newAssignments.filter((a) => a.resourceType === resourceType).map((a) => `${a.resourceId}:${a.resourceRole}`),
    );

    // Quick size check - if sizes differ, definitely changed
    if (oldIds.size !== newIds.size) return true;

    // Deep comparison - check if all old IDs exist in new set
    for (const id of oldIds) {
        if (!newIds.has(id)) return true;
    }

    return false;
}

/**
 * Computes which resource types have permission changes between two assignment sets.
 *
 * This is a convenience wrapper around hasResourceTypeChanges that checks multiple
 * resource types at once. Useful for determining which resource lists need invalidation.
 *
 * @param oldAssignments - Previous role assignments
 * @param newAssignments - Current role assignments
 * @returns Object with boolean flags for each resource type
 *
 * @example
 * ```typescript
 * const diff = computePermissionDiff(oldAssignments, newAssignments);
 * if (diff.projectsChanged) {
 *   await queryClient.invalidateQueries({ queryKey: ["projects"] });
 * }
 * if (diff.runnersChanged) {
 *   await queryClient.invalidateQueries({ queryKey: ["runners"] });
 * }
 * ```
 */
export function computePermissionDiff(
    oldAssignments: PlainRoleAssignment[],
    newAssignments: PlainRoleAssignment[],
): {
    projectsChanged: boolean;
    runnersChanged: boolean;
} {
    return {
        projectsChanged: hasResourceTypeChanges(oldAssignments, newAssignments, ResourceType.PROJECT),
        runnersChanged: hasResourceTypeChanges(oldAssignments, newAssignments, ResourceType.RUNNER),
    };
}
